// useRestaurantSearch — state + side-effect + handler hook for RestaurantSearch.
//
// Extracted VERBATIM from `components/RestaurantSearch.js` (behavior-preserving
// refactor, issue #240). Owns every piece of stateful / side-effecting behavior
// the source held inline in the `RestaurantSearch` function — a single cohesive
// hook, matching the granularity judgment of `manualTripCreator/hooks/`.
//
// State (same initial values as the source):
//   - `restaurants`         (search results list, init [])
//   - `loading`             (in-flight flag, init false)
//   - `error`               (error banner string, init null)
//   - `showFilters`         (filters panel toggle, init false)
//   - `searchLocation`      ('airport' | 'hotel', init 'airport')
//   - `selectedHotel`       (lifted from trip.hotels[0], init null)
//   - `hasSearched`         (whether a search has run, init false)
//   - `showLocationDialog`  (location-picker dialog toggle, init false)
//   - `isCollapsed`         (results collapse toggle, init false)
//   - `filters`             (active filter values, init getDefaultFilters())
//
// Effects + handlers reproduce the source EXACTLY:
//   - HOTEL EXTRACTION effect: on mount / `trip` change, when
//     `trip?.hotels?.length > 0`, lift `trip.hotels[0]` into `selectedHotel`.
//     Dep array `[trip]` preserved.
//   - `performSearch(locationType = null)` is a `useCallback` with the SAME deps
//     `[location, selectedHotel, searchLocation, filters]`. It resolves
//     `searchLoc = locationType || searchLocation`, bails when there is neither a
//     `location` nor a `selectedHotel`, then runs the loading/try/catch/finally
//     exactly as the source did, assembling params via `buildSearchParams` and
//     calling `searchRestaurants`. On failure it logs and sets the same error
//     string. Preserved verbatim — effect timing depends on this identity.
//   - FILTER-REFETCH effect with the SAME deps
//     `[filters, searchLocation, hasSearched, location, selectedHotel, performSearch]`:
//     re-runs `performSearch()` when `hasSearched && (location || selectedHotel)`.
//   - `handleFilterChange(key, value)` merges one field into `filters`.
//   - `clearFilters()` resets `filters` to `getDefaultFilters()` (the source did
//     `{ ...defaultFilters }`; the factory returns the same fresh copy).
//   - `handleLocationSelect(locationType)` sets the search location, closes the
//     dialog, marks `hasSearched`, and triggers `performSearch(locationType)`.
//   - `handleFindRestaurantsClick()` sets the "No location available for search"
//     error when there is no location and no hotel, otherwise opens the dialog.
//   - `handleClearSearch()` performs the full reset the source did
//     (hasSearched=false, restaurants=[], error=null, filters→getDefaultFilters(),
//     searchLocation→'airport', isCollapsed=false, showFilters=false).
//   - Derived values `hasActiveFilters = checkHasActiveFilters(filters)` and
//     `hasHotel = checkHasHotel(selectedHotel)` use the logic helpers.
//
// FEATURE GATE: the `if (!isFeatureEnabled('OPENTABLE_ENABLED')) return null;`
// short-circuit stays in the CONTAINER (task 15.7), NOT here — it controls
// render, not state. Per the Rules of Hooks, the source calls every
// useState/useEffect/useCallback BEFORE that early return, so the container MUST
// call `useRestaurantSearch()` UNCONDITIONALLY and only then early-return on the
// flag (exactly as the source does).
//
// `use`-prefixed, contains no JSX and performs no rendering.
//
// Validates: Requirements 3.2, 3.4, 1.4

import { useState, useEffect, useCallback } from 'react';
import { searchRestaurants } from '../../../services/opentableService';
import { buildSearchParams } from '../logic/buildSearchParams';
import { getDefaultFilters } from '../logic/defaultFilters';
import { checkHasActiveFilters } from '../logic/checkHasActiveFilters';
import { checkHasHotel } from '../logic/checkHasHotel';

/**
 * Owns the restaurant-search state, the hotel-extraction and filter-refetch
 * effects, the search runner, and all user-action handlers.
 *
 * The container must call this hook UNCONDITIONALLY (before the
 * `OPENTABLE_ENABLED` feature-gate early return) to honor the Rules of Hooks.
 *
 * @param {Object} location - The trip location ({ latitude?, longitude?, airportCode?, address? }) or null.
 * @param {Object} trip - The trip ({ hotels?: Array }) or null; first hotel is lifted into state.
 * @param {Object} [initialFilters] - Optional filter overrides merged onto
 *   getDefaultFilters() at mount (e.g. from a taste profile or a detected
 *   dining moment). Omit for the default fresh-filters behavior.
 * @param {string} [initialSearchLocation] - Optional 'airport' | 'hotel' to
 *   seed `searchLocation` and `hasSearched` at mount, skipping the "Find
 *   Restaurants" button + location-dialog step and letting the existing
 *   filter-refetch effect run the first search automatically. Omit for the
 *   default (dialog-driven) behavior.
 * @returns {{
 *   restaurants: Array<Object>,
 *   loading: boolean,
 *   error: ?string,
 *   showFilters: boolean,
 *   setShowFilters: Function,
 *   searchLocation: string,
 *   setSearchLocation: Function,
 *   selectedHotel: ?Object,
 *   hasSearched: boolean,
 *   showLocationDialog: boolean,
 *   setShowLocationDialog: Function,
 *   isCollapsed: boolean,
 *   setIsCollapsed: Function,
 *   filters: Object,
 *   hasActiveFilters: boolean,
 *   hasHotel: boolean,
 *   performSearch: (locationType?: ?string) => Promise<void>,
 *   handleFilterChange: (key: string, value: *) => void,
 *   clearFilters: () => void,
 *   handleLocationSelect: (locationType: string) => void,
 *   handleFindRestaurantsClick: () => void,
 *   handleClearSearch: () => void
 * }} The search state and actions.
 */
export function useRestaurantSearch(location, trip, initialFilters, initialSearchLocation) {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [searchLocation, setSearchLocation] = useState(initialSearchLocation || 'airport');
  const [selectedHotel, setSelectedHotel] = useState(null);
  // Seeding this true (only when a caller supplies initialSearchLocation)
  // lets the existing filter-refetch effect below run the first search on
  // mount, with zero new effects — it already fires unconditionally on
  // mount and re-runs on searchLocation/filters changes.
  const [hasSearched, setHasSearched] = useState(() => Boolean(initialSearchLocation));
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [filters, setFilters] = useState(() => (
    initialFilters ? { ...getDefaultFilters(), ...initialFilters } : getDefaultFilters()
  ));

  // Extract first hotel from trip
  useEffect(() => {
    if (trip && trip.hotels && trip.hotels.length > 0) {
      setSelectedHotel(trip.hotels[0]);
    }
  }, [trip]);

  const performSearch = useCallback(async (locationType = null) => {
    const searchLoc = locationType || searchLocation;
    if (!location && !selectedHotel) return;

    setLoading(true);
    setError(null);

    try {
      const searchParams = buildSearchParams(searchLoc, location, selectedHotel, filters);
      const results = await searchRestaurants(searchParams);
      setRestaurants(results);
    } catch (err) {
      console.error('Error searching restaurants:', err);
      setError('Failed to search restaurants. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [location, selectedHotel, searchLocation, filters]);

  // Update search when filters change (only if search has been performed)
  useEffect(() => {
    if (hasSearched && (location || selectedHotel)) {
      performSearch();
    }
  }, [filters, searchLocation, hasSearched, location, selectedHotel, performSearch]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters(getDefaultFilters());
  };

  const hasActiveFilters = checkHasActiveFilters(filters);

  const hasHotel = checkHasHotel(selectedHotel);

  const handleLocationSelect = (locationType) => {
    setSearchLocation(locationType);
    setShowLocationDialog(false);
    setHasSearched(true);
    performSearch(locationType);
  };

  const handleFindRestaurantsClick = () => {
    if (!location && !selectedHotel) {
      setError('No location available for search');
      return;
    }
    setShowLocationDialog(true);
  };

  const handleClearSearch = () => {
    setHasSearched(false);
    setRestaurants([]);
    setError(null);
    setFilters(getDefaultFilters());
    setSearchLocation('airport');
    setIsCollapsed(false);
    setShowFilters(false);
  };

  return {
    // State
    restaurants,
    loading,
    error,
    showFilters,
    setShowFilters,
    searchLocation,
    setSearchLocation,
    selectedHotel,
    hasSearched,
    showLocationDialog,
    setShowLocationDialog,
    isCollapsed,
    setIsCollapsed,
    filters,
    // Derived
    hasActiveFilters,
    hasHotel,
    // Search runner + handlers
    performSearch,
    handleFilterChange,
    clearFilters,
    handleLocationSelect,
    handleFindRestaurantsClick,
    handleClearSearch
  };
}

export default useRestaurantSearch;
